'use client';

import Link from 'next/link';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, ArrowRight, LocateFixed, Loader, HelpCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import tips from '@/app/lib/electrician-tips.json';

function RandomTip() {
  const [tip, setTip] = useState<string>('');

  useEffect(() => {
    setTip(tips[Math.floor(Math.random() * tips.length)]);
  }, []);

  if (!tip) return null;

  return (
    <Card className="max-w-2xl mx-auto bg-primary/10 border-primary/20 p-4 text-center">
        <HelpCircle className="mx-auto h-6 w-6 text-primary mb-2" />
        <CardTitle className="text-foreground text-lg">Wist je dat?</CardTitle>
        <p className="text-muted-foreground text-sm mt-2">{tip}</p>
    </Card>
  );
}

export default function ElectricianProvinceClient() {
  const [suggestedProvince, setSuggestedProvince] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const { toast } = useToast();

  const provinces = [
    { name: 'Antwerpen', href: '/elektriciens-in-antwerpen', active: true },
    { name: 'Limburg', href: '/elektriciens-in-limburg', active: true },
    { name: 'Oost-Vlaanderen', href: '#', active: false },
    { name: 'Vlaams-Brabant', href: '#', active: false },
    { name: 'West-Vlaanderen', href: '#', active: false },
  ];

  const handleLocate = () => {
    setIsLocating(true);
    // Voor nu een simpele simulatie of suggestie naar Antwerpen omdat die klaar is
    setTimeout(() => {
        setIsLocating(false);
        setSuggestedProvince('Antwerpen');
        toast({
            title: "Suggestie gevonden",
            description: "We raden u aan om te starten in de provincie Antwerpen of Limburg.",
        });
    }, 1000);
  };

  return (
    <div className="bg-muted min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <Zap className="mx-auto h-12 w-12 text-primary mb-4" />
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
            Elektriciens in Vlaanderen
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-muted-foreground">
            Kies uw provincie om direct een erkend elektricien in uw buurt te vinden.
          </p>
        </div>

        <Card className="max-w-md mx-auto shadow-lg mb-10">
            <CardContent className="p-6">
                 <Button className="w-full" size="lg" onClick={handleLocate} disabled={isLocating}>
                    {isLocating ? <Loader className="mr-2 animate-spin" /> : <LocateFixed className="mr-2" />}
                    Vind elektriciens in mijn buurt
                </Button>
            </CardContent>
        </Card>
        
        <RandomTip />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 mb-12">
          {provinces.map((province) => (
              <div key={province.name} className="relative">
                 {province.name === suggestedProvince && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 bg-primary">Suggestie</Badge>
                )}
                <Card className={`group h-full flex flex-col ${!province.active ? 'bg-muted/60 opacity-80' : 'hover:border-primary transition-colors'}`}>
                    <CardHeader>
                        <CardTitle className="text-2xl">{province.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col justify-end">
                        {province.active ? (
                            <Button asChild className="w-full mt-4">
                                <Link href={province.href}>
                                    Bekijk elektriciens <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                        ) : (
                            <Button className="w-full mt-4" disabled variant="secondary">
                                Binnenkort beschikbaar
                            </Button>
                        )}
                    </CardContent>
                </Card>
              </div>
          ))}
        </div>
      </div>
    </div>
  );
}
